import { useEffect, useRef } from "react";
import dayjs from "dayjs";
import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "../styles/tour.css";
import { useFetchUser } from "./useFetchUser";

const seenKey = (tour: string) => `palomma:tour-seen:${tour}`;

// StrictMode double-invokes effects, and a page tour must not open on top of
// the welcome tour, so a single module-level flag guards every tour; a tour
// that arrives while another runs waits its turn.
let active = false;
const waiting: (() => void)[] = [];

// Anchors appear only once the page finishes loading its data, so wait for
// them instead of firing a half-empty tour; give up if they never show up.
const WAIT_FOR_ANCHORS_MS = 1500;

export const hasSeenTour = (tour: string) =>
  !!localStorage.getItem(seenKey(tour));

export const forgetTour = (tour: string) =>
  localStorage.removeItem(seenKey(tour));

const seenListeners = new Set<(tour: string) => void>();

export const onTourSeen = (tour: string, callback: () => void) => {
  const listener = (seen: string) => seen === tour && callback();
  seenListeners.add(listener);
  return () => void seenListeners.delete(listener);
};

export function useTour(
  tour: string,
  steps: DriveStep[],
  enabled: boolean,
  onDone?: () => void
) {
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;
  const createdAt = useFetchUser().user?.createdAt;

  useEffect(() => {
    const veteran =
      !!createdAt && createdAt < dayjs().subtract(2, "month").toISOString();
    if (!enabled || !tour || veteran || hasSeenTour(tour)) return;

    let cancelled = false;
    let observer: MutationObserver | undefined;
    let timer: number | undefined;

    const isVisible = (selector: string) => {
      const box = document.querySelector(selector)?.getBoundingClientRect();
      return !!box && box.width > 0 && box.height > 0;
    };

    const onScreen = () =>
      steps.filter(({ element }) => !element || isVisible(element as string));

    const stop = () => {
      observer?.disconnect();
      window.clearTimeout(timer);
    };

    const start = () => {
      const visible = onScreen();
      stop();
      if (cancelled || visible.length < 2) return;
      if (active) return void waiting.push(start);

      active = true;
      driver({
        steps: visible,
        showProgress: true,
        allowClose: false,
        nextBtnText: "Siguiente",
        prevBtnText: "Atrás",
        doneBtnText: "Entendido",
        progressText: "{{current}} de {{total}}",
        popoverClass: "palomma-tour",
        overlayColor: "#11032e",
        overlayOpacity: 0.6,
        stageRadius: 8,
        stagePadding: 6,
        onDestroyed: () => {
          localStorage.setItem(seenKey(tour), "true");
          active = false;
          seenListeners.forEach((listener) => listener(tour));
          onDoneRef.current?.();
          while (waiting.length && !active) waiting.shift()?.();
        },
      }).drive();
    };

    if (onScreen().length === steps.length) {
      start();
    } else {
      observer = new MutationObserver(() => {
        if (onScreen().length === steps.length) start();
      });
      observer.observe(document.body, { childList: true, subtree: true });
      timer = window.setTimeout(start, WAIT_FOR_ANCHORS_MS);
    }

    return () => {
      cancelled = true;
      stop();
    };
  }, [tour, steps, enabled, createdAt]);
}
